import React from 'react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  FAMILY_DISEASE_HISTORY_TITLE,
  FAMILY_RELATIVE_KEYS,
  FAMILY_RELATIVE_LABELS,
  type FamilyDiseaseHistory,
  type FamilyRelativeKey,
} from '@/lib/familyDiseaseHistory';

type FamilyDiseaseHistoryFieldsProps = {
  value: FamilyDiseaseHistory;
  onChange: (next: FamilyDiseaseHistory) => void;
  error?: string;
};

export function FamilyDiseaseHistoryFields({
  value,
  onChange,
  error,
}: FamilyDiseaseHistoryFieldsProps) {
  const updateRow = (
    key: FamilyRelativeKey,
    patch: Partial<FamilyDiseaseHistory[FamilyRelativeKey]>,
  ) => {
    onChange({
      ...value,
      [key]: {
        ...value[key],
        ...patch,
      },
    });
  };

  return (
    <div className="space-y-4">
      <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex">
        {FAMILY_DISEASE_HISTORY_TITLE}
        <span className="text-red-500 ml-1">*</span>
      </Label>

      <div className="space-y-4">
        {FAMILY_RELATIVE_KEYS.map((key) => {
          const row = value[key];
          const label = FAMILY_RELATIVE_LABELS[key];
          const radioName = `family-history-${key}`;
          return (
            <div
              key={key}
              className="rounded-xl border border-gray-100 dark:border-neutral-800 p-3 space-y-2 bg-gray-50/50 dark:bg-neutral-800/20"
            >
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{label}</p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name={radioName}
                    checked={row.hasDisease === true}
                    onChange={() => updateRow(key, { hasDisease: true })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                  />
                  Sim
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name={radioName}
                    checked={row.hasDisease === false}
                    onChange={() => updateRow(key, { hasDisease: false, description: '' })}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900"
                  />
                  Não
                </label>
              </div>
              {row.hasDisease === true && (
                <textarea
                  value={row.description}
                  onChange={(event) => updateRow(key, { description: event.target.value })}
                  placeholder={`Descreva as doenças de ${label.toLowerCase()}...`}
                  rows={2}
                  className={cn(
                    'w-full rounded-md border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-neutral-900 resize-none',
                    error
                      ? 'border-red-500 dark:border-red-500/50'
                      : 'border-gray-200 dark:border-neutral-700',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && <span className="block text-xs text-red-600">{error}</span>}
    </div>
  );
}
